// Zod schemas for customer forms — uses REAL BE fields from
// backend/internal/core/customer/model.go (Customer, UpsertInput, PatchInput).
//
// Phone regex: Vietnamese phones accepted as raw input.
// normalizePhone mirrors gaonau-customer-sdk.js normalizePhone:
//   strip non-digits → strip leading 84 (if len>=11) → strip leading 0
//   → require 9–10 remaining digits → prepend "84"
// The raw UI value is validated here; BE normalizes on its side.

import { z } from "zod";

// Loose PHONE_RE: legacy export for order form guard parity.
export const PHONE_RE = /^(?:\+?84|0)\d{9,10}$/;
const CENTER_E164_RE = /^[1-9]\d{7,14}$/;

function stripPhoneFormatting(raw: string): string {
  return raw.trim().replace(/[ \t.\-()]/g, "");
}

function normalizeVietnamCenterPhone(raw: string): string {
  let p = stripPhoneFormatting(raw);
  if (p.startsWith("+84")) p = `84${p.slice(3)}`;
  else if (p.startsWith("0") && p.length === 10) p = `84${p.slice(1)}`;
  else if (p.startsWith("84") && p.length >= 11) {
    // already close to Center's Vietnam shape
  } else {
    return "";
  }
  if (p.length === 12 && p.startsWith("840")) p = `84${p.slice(3)}`;
  if (p.length !== 11 || !/^\d+$/.test(p)) return "";
  return p;
}

export function normalizeCenterPhone(raw: string): string {
  const vn = normalizeVietnamCenterPhone(raw);
  if (vn) return vn;
  let p = stripPhoneFormatting(raw);
  if (!p) return "";
  if (p.startsWith("00")) p = `+${p.slice(2)}`;
  if (!p.startsWith("+")) {
    if (!/^\d+$/.test(p)) return "";
    if (p.startsWith("0904") && p.length === 11) return `63${p.slice(1)}`;
    return "";
  }
  let digits = p.slice(1);
  if (!CENTER_E164_RE.test(digits)) return "";
  if (digits.startsWith("630904") && digits.length === 13) digits = `63${digits.slice(3)}`;
  if (digits.startsWith("562") && digits.length === 10) digits = `5622${digits.slice(3)}`;
  return digits;
}

export function isValidCenterPhone(raw: string): boolean {
  return normalizeCenterPhone(raw) !== "";
}

// YYYY-MM-DD (BE rejects malformed dates, but reject early on the form too).
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Well-known tag values; the input is open-set but the FE has UI for these.
export const TAG_VIP = "vip";
export const TAG_BLACKLIST = "blacklist";
// "KH cũ tiềm năng" — an existing customer manually flagged for re-engagement.
// Mirrors customer.TagPotential in the BE model.
export const TAG_POTENTIAL = "potential";

// Customer-segment filter values for GET /customers/page (?segment=). "new" =
// KH mới (total_orders <= 1). Mirrors customer.SegmentNew in the BE.
export const SEGMENT_NEW = "new";

// ══════════ CUSTOMER UPSERT FORM ══════════
// Used for create / update of a customer record.
// Mirrors CustomerInput (UpsertInput in model.go).
export const CustomerFormSchema = z.object({
  phone: z
    .string()
    .min(1, "Số điện thoại không được để trống")
    .refine(isValidCenterPhone, "Số điện thoại không hợp lệ (VD: 0912345678 hoặc +56 2 2670 7075)"),
  name: z.string().min(1, "Tên không được để trống"),
  gender: z.string().optional(),
  email: z
    .string()
    .email("Email không hợp lệ")
    .optional()
    .or(z.literal("")),
  branch: z.string().optional(),
  // Nguồn khách — chỉ ô của hộp sửa (trang hồ sơ khách) hiện; tạo mới để trống.
  source: z.string().optional(),
  type: z.string().optional(),
  company: z.string().optional(),
  notes: z.string().optional(),
  birthday: z
    .string()
    .regex(DATE_RE, "Sinh nhật phải có dạng YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
  anniversary: z
    .string()
    .regex(DATE_RE, "Kỷ niệm phải có dạng YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
  tags: z.array(z.string()).optional(),
  // Tên thật + thông tin xuất hoá đơn (migration 0609, CEO 24/10/2026). Chỉ hộp
  // SỬA hiện các ô này — đường tạo khách mới chưa nhận chúng.
  legalName: z.string().max(200, "Tên thật tối đa 200 ký tự").optional(),
  invoiceAddress: z.string().max(300, "Địa chỉ tối đa 300 ký tự").optional(),
  idKind: z.enum(["", "cccd", "passport"]).optional(),
  idNumber: z.string().optional(),
  idNationality: z.string().max(60, "Quốc tịch tối đa 60 ký tự").optional(),
}).superRefine((v, ctx) => {
  const num = (v.idNumber ?? "").replace(/\s+/g, "").toUpperCase();
  // Bản che (••••1234) gửi lại = không đổi số — máy chủ hiểu.
  if (num.includes("•")) return;
  if (!v.idKind && num) {
    ctx.addIssue({ code: "custom", path: ["idNumber"], message: "Chọn loại giấy tờ (CCCD hoặc hộ chiếu) cho số đã nhập" });
  }
  if (v.idKind === "cccd" && !/^[0-9]{12}$/.test(num)) {
    ctx.addIssue({ code: "custom", path: ["idNumber"], message: "Số CCCD phải đúng 12 chữ số" });
  }
  if (v.idKind === "passport") {
    if (!/^[A-Z0-9]{6,20}$/.test(num)) {
      ctx.addIssue({ code: "custom", path: ["idNumber"], message: "Số hộ chiếu có 6–20 chữ cái hoặc chữ số" });
    }
    if (!(v.idNationality ?? "").trim()) {
      ctx.addIssue({ code: "custom", path: ["idNationality"], message: "Hộ chiếu cần ghi quốc tịch" });
    }
  }
});

export type CustomerFormValues = z.infer<typeof CustomerFormSchema>;

// ══════════ CUSTOMER PATCH FORM ══════════
// For inline-editable detail fields — only updatable fields.
// Mirrors CustomerPatch (PatchInput in model.go).
export const CustomerPatchSchema = z.object({
  name: z.string().min(1, "Tên không được để trống").optional(),
  email: z
    .string()
    .email("Email không hợp lệ")
    .optional()
    .or(z.literal("")),
  gender: z.string().optional(),
  type: z.string().optional(),
  company: z.string().optional(),
  branch: z.string().optional(),
  notes: z.string().optional(),
  customerCode: z.string().optional(),
  source: z.string().optional(),
  birthday: z
    .string()
    .regex(DATE_RE, "Sinh nhật phải có dạng YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
  anniversary: z
    .string()
    .regex(DATE_RE, "Kỷ niệm phải có dạng YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
  tags: z.array(z.string()).optional(),
  crmProfile: z.record(z.string(), z.unknown()).optional(),
});

export type CustomerPatchValues = z.infer<typeof CustomerPatchSchema>;

// ══════════ CUSTOMERS LIST SEARCH (URL state) ══════════
// Serializes the list filters into the URL so navigating into a customer detail
// and back restores the exact same search/tag/brand/page. Shared by the list
// route and the detail route (detail carries the params through untouched).
export type CustomersListSearch = {
  q?: string | undefined;
  tag?: string | undefined;
  brand?: string | undefined;
  segment?: "new" | undefined;
  today?: boolean | undefined;
  offset?: number | undefined;
};

// parseCustomersListSearch validates raw URL search into CustomersListSearch.
// Empty/invalid values collapse to undefined so they drop out of the URL.
export function parseCustomersListSearch(search: Record<string, unknown>): CustomersListSearch {
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim() : undefined;
  const offsetRaw = Number(search.offset);
  const offset = Number.isFinite(offsetRaw) && offsetRaw > 0 ? Math.floor(offsetRaw) : undefined;
  return {
    q: str(search.q),
    tag: str(search.tag),
    brand: str(search.brand),
    segment: search.segment === SEGMENT_NEW ? SEGMENT_NEW : undefined,
    today: search.today === true || search.today === "true" ? true : undefined,
    offset,
  };
}
